import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import {
  MatFormFieldModule,
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
} from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { AutoRedemptionSettingsViewModule } from '@features/auto-redemption-settings-ui/auto-redemption-settings-view.module';
import { EnrollmentFormModule } from '@features/enrollment-ui/enrollment-form/enrollment-form.module';
import { PromocodeBannerModule } from '@features/promocode-banner/promocode-banner.module';
import { ContactUsEffects } from '@features/static-pages/store/effects/contact-us.effects';
import { reducers } from '@features/static-pages/store/reducers/index';
import { UserInfoModule } from '@features/user-info/user-info.module';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { MainContainerComponent } from '@rd/core/containers/main-container/main-container.component';
import { TransactionDetailsFormatterService } from '@rd/core/services/transaction-details-formatter/transaction-details-formatter.service';
import { DataTestidDirective } from '@rd/shared-legacy/directives/data-testid/data-testid.directive';
import { SharedModule } from '@rd/shared-legacy/shared.module';
import { BannerImageModule } from '@rd/shared-legacy/widgets/banner-image/banner-image.module';
import { AnzRCEnrollmentComponent } from '@rd/wl/anz/views/redeem-cash-v2/views/enrollment/enrollment.component';
import { MarkdownModule } from 'ngx-markdown';
import { AnzAppComponent } from '../app.component';
import { AnzAlertBannerComponent } from '../components/alert-banner/alert-banner.component';
import { AnzAuthSecureRouteDialogComponent } from '../components/auth-secure-route-dialog/auth-secure-route-dialog.component';
import { AnzBannerComponent } from '../components/banner/banner.component';
import { AnzBenefitsComponent } from '../components/benefits/benefits.component';
import { AnzBypassBlockComponent } from '../components/bypass-block/bypass-block.component';
import { AnzCampaignBannerHomeComponent } from '../components/campaign-banner-home/campaign-banner-home.component';
import { AnzCardSelectorComponent } from '../components/card-selector/card-selector.component';
import { ANZEnrollmentFormComponentComponent } from '../components/enrollment-form-component/enrollment-form.component';
import { ANZEnrollmentLinkAccountDetailFormComponent } from '../components/enrollment-link-account-detail-form/link-account-detail-form.component';
import { AnzFeaturedPartnersComponent } from '../components/featured-partners/featured-partners.component';
import { AnzFooterComponent } from '../components/footer/footer.component';
import { AnzHomePageBannerComponent } from '../components/homepage-banner/homepage-banner.component';
import { AnzInfoPanelAccordionComponent } from '../components/info-panel-accordion/info-panel-accordion.component';
import { AnzQuickAccessComponent } from '../components/quick-access/quick-access.component';
import { AnzScHeaderIconComponent } from '../components/sc-header-icon/sc-header-icon.component';
import { AnzTooltipsComponent } from '../components/tooltips/tooltips.component';
import { ANZVelocityAutoRedemptionSetupPromptComponent } from '../components/velocity-auto-redemption-setup-prompt/velocity-auto-redemption-setup-prompt.component';
import { AnzDesktopHeaderComponent } from '../containers/desktop-header/desktop-header.component';
import { AnzMobileHeaderComponent } from '../containers/mobile-header/mobile-header.component';
import { AnzSideNavContentComponent } from '../containers/side-nav-content/side-nav-content.component';
import { AnzPromocodeBannerUiModule } from '../promocode-banner-ui/promocode-banner-ui.module';
import { ANZFeatureFlagService } from '../services/anz-feature-flag.service';
import { AnzHomePageService } from '../services/anz-home-page.service';
import { AnzNavLinksService } from '../services/anz-nav-links.service';
import { AnzPointsExpiryService } from '../services/expiry-date/points-expiry.service';
import { AnzTransactionDetailsFormatterService } from '../services/transaction-details-formatter/transaction-details-formatter.service';
import { AnzGiftCardUnavailableComponent } from '../views/gift-card-unavailable/gift-card-unavailable.component';
import { AnzHomePageComponent } from '../views/home-page/home-page.component';
import { AnzPageNotFoundComponent } from '../views/page-not-found/page-not-found.component';
import { ANZAutoredemptionEnrollmentComponent } from '../views/redeem-cash-v2/views/auto-redemption-enrollment/auto-redemption-enrollment.component';

const views = [
  AnzAppComponent,
  AnzHomePageComponent,
  AnzPageNotFoundComponent,
  AnzGiftCardUnavailableComponent,
  ANZAutoredemptionEnrollmentComponent,
];

const declaredComponents: any[] = [
  AnzAuthSecureRouteDialogComponent,
  AnzBannerComponent,
  AnzBenefitsComponent,
  AnzCardSelectorComponent,
  AnzDesktopHeaderComponent,
  AnzFeaturedPartnersComponent,
  AnzInfoPanelAccordionComponent,
  AnzFooterComponent,
  AnzMobileHeaderComponent,
  AnzQuickAccessComponent,
  AnzSideNavContentComponent,
  AnzBypassBlockComponent,
  AnzCampaignBannerHomeComponent,
  AnzScHeaderIconComponent,
  ANZEnrollmentLinkAccountDetailFormComponent,
  ANZEnrollmentFormComponentComponent,
  AnzRCEnrollmentComponent,
  AnzTooltipsComponent,
  ANZVelocityAutoRedemptionSetupPromptComponent,
];

const exportComponents: any[] = [];

@NgModule({
  declarations: [...views, ...declaredComponents],
  imports: [
    AnzAlertBannerComponent,
    AnzHomePageBannerComponent,
    BannerImageModule,
    PromocodeBannerModule,
    AnzPromocodeBannerUiModule,
    UserInfoModule,
    AutoRedemptionSettingsViewModule,
    CommonModule,
    MainContainerComponent,
    MatCheckboxModule,
    MatSelectModule,
    MatFormFieldModule,
    MatExpansionModule,
    MatInputModule,
    MatSidenavModule,
    MatRadioModule,
    RouterModule,
    SharedModule,
    EnrollmentFormModule,
    DataTestidDirective,
    MarkdownModule.forRoot(),
    TranslateModule.forChild(),
    StoreModule.forFeature('staticPages', reducers),
    EffectsModule.forFeature([ContactUsEffects]),
  ],
  exports: [...exportComponents],
  providers: [
    AnzHomePageService,
    AnzNavLinksService,
    AnzPointsExpiryService,
    ANZFeatureFlagService,
    SharedModule,
    {
      provide: TransactionDetailsFormatterService,
      useClass: AnzTransactionDetailsFormatterService,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        hideRequiredMarker: true,
      },
    },
  ],
})
export class AnzModule {}
